import React from "react";
import enableConditionalFields, {
  compareEqualConditional,
  compareNotEqualConditional,
  getFieldValue
} from "../utils/enable-conditional-fields";
import extractFieldValues from "../utils/extract-field-values";
import { tdLog } from "../utils/td-logger";
import { buildTrackingEvent } from "../utils/tracking-events";
import env from "../utils/env";
import { setSharedScreens } from "../utils/shared-screens";
import { updateFieldStates } from "./helpers/action-event-states";
import { fileToImage, dataUrlToImage, createThumbnail } from "../utils/images";
import PdfThumbnail from "../assets/images/icn_pdf.png";
import DocThumbnail from "../assets/images/icn_doc.png";
import XlsThumbnail from "../assets/images/icn_xls.png";
import {
  CALL_TO_ACTION_BUTTON_TYPES,
  PRE_PHOTO_MODAL_SCREENS,
  ACTION_PARAMS,
  SHORTEN_FLOW_DEPENDANT_SCREENS,
  PREVENT_SELECTED_FIELD_OPTION_UPDATE,
  SCREENS_WITH_MULTIPLE_PHOTO_PICKERS,
  UNIFIED_PROFILE_PARAMS
} from "../utils/constants";
import {
  removeItemFromIndexedDB,
  setItemToIndexedDB
} from "../utils/localforage-utils";
import { isSkipableUrl } from "../utils/back-button-helper";
import { isResettableScreen } from "../utils/is-resettable-screen";
import { SnapshotModelFactory } from "./screen-snapshot-models/SnapshotModelFactory.js";
import OriginalSnapshotData from "./screen-snapshot-models/original-snapshot-data";
import showExitDislog from "../../src/screens/helpers/unified_profile/unsaved-close-dialog";
import { downloadFile } from "../utils/download-file";

/**
 * Default Controller that other controllers extend from. Preprocesses incoming data, handles events, and returns
 * a screen component.
 * @class BaseViewController
 * @property {object} screen Contains screen content and settings to be rendered
 * @property {object} appData Contains data on flow and screen, as well as methods (i.e. screen data, history,
 * validators)
 * @property {object} components All default theme components from [Default Themes]{@link module:theme/default}
 * @property {React.Context} dialog instantiated dialog context created in [ScreenBuilder]{@link ScreenBuilder}
 * via [useDialog]{@link module:context/messaging-layer#useDialog}
 * @property {bool} useBrowserNavigation If true, browser back button navigates to previous screen in WebSDK flow;
 * if false browser back button will navigate to page before entering WebSDK
 * @property {Function} isFieldsValid Accepts appData and returns boolean value of whether all fields are valid
 * @property {React.Component} TdFlowApp
 * @returns {React.Component} Returns [Screen]{@link Screen} component
 */
class BaseViewController {
  constructor({
    screen,
    appData,
    components,
    dialog,
    useBrowserNavigation,
    isFieldsValid,
    TdFlowApp
  }) {
    this.screen = screen;
    this.appData = appData;
    this.components = components;
    this.dialog = dialog;
    this.useBrowserNavigation = useBrowserNavigation;
    this.isFieldsValid = isFieldsValid;
    this.TdFlowApp = TdFlowApp;

    if (this.screen?.params?.includes("TDScreenOptionConfirmUnsavedChanges")) {
      this.originalSnapshot = new OriginalSnapshotData().createOriginalSnapshot(
        this.appData
      );
    }

    if (this.useBrowserNavigation && !appData.properLink) {
      window.onhashchange = this.hashChangeHandler.bind(this);
    }
  }

  hashChangeHandler(event) {
    const newScreenName = event.newURL.split("#")[1];
    // user attempts to return on previous page before WebSDK
    if (!newScreenName) {
      const previousScreen = this.appData.history[1];

      if (previousScreen && previousScreen.name === "InboxScreen") {
        this.handleBackButtonClick();
        return;
      }

      window.onhashchange = null;
      return;
    }
    const currentScreenIndex = this.appData.screensHistoryFlow
      .map(screen => screen.name)
      .lastIndexOf(this.screen.name);
    let newScreenIndex = this.appData.screensHistoryFlow
      .map(screen => screen.name)
      .indexOf(newScreenName, currentScreenIndex);
    if (newScreenIndex === -1) {
      newScreenIndex = this.appData.screensHistoryFlow
        .map(screen => screen.name)
        .lastIndexOf(newScreenName);
    }

    if (newScreenName !== this.screen.name) {
      if (newScreenIndex === -1) {
        this.returnToCurrentPage();
        return;
      }

      if (newScreenIndex < currentScreenIndex) {
        if (this.appData.history.length === 1) {
          window.history.go(1 - window.history.length);
        } else if (
          (this.screen.params || "").includes(
            "TDScreenOptionConsultationPhotoModal"
          )
        ) {
          this.appData.prevScreen(2);
        } else {
          this.handleBackButtonClick();
        }
      } else {
        if (!this.isFieldsValid(this.appData)) {
          this.returnToCurrentPage();
          return;
        }

        let newValues = { ...this.appData.values };

        newValues = this.prepareSubmit(newValues);

        // We need to preserve the screens below for future usage.
        setSharedScreens(this.appData.data.screens);

        this.appData.nextScreen({
          data: this.appData.data,
          screenIndex:
            this.appData.screensHistoryFlow[currentScreenIndex]?.index,
          action: this.appData.screensHistoryFlow[currentScreenIndex]?.action,
          formState: newValues,
          replaceHistory: 0,
          isUnifiedRegistration: this.appData.isUnifiedRegistration
        });
      }
    }
  }

  returnToCurrentPage() {
    const baseUrl = window.location.href.split("#")[0];
    window.location.assign(`${baseUrl}#${this.screen.name}`);
  }

  /**
   * Called in [ScreenBuilder]{@link ScreenBuilder}
   * Calls [preprocessAppData]{@link preprocessAppData} and [preprocessScreenData]{@link preprocessScreenData}
   * and to add and format fields
   * Populates redux state with initial field values to initialize screen
   * @method BaseViewController#execute
   * @returns [renderScreen]{@link renderScreen}
   */
  execute() {
    // exit WebSDK on attestation submission
    if (
      this.screen.name === "VisitUscStudyAttestation" &&
      (this.appData.data?.actions?.[0]?.type === "pop_flow" ||
        !this.appData.data?.show_screens)
    ) {
      this.appData.onExit();
      return null;
    }

    // 1. Pre-process screen/app data. This can be used to add / massage fields.
    this.preprocessAppData();
    this.preprocessScreenData();

    const isModal = this.screen.type === "modal_screen";

    if (
      !this.appData.screenInitialized ||
      (isModal && !this.appData.dialogInitialized)
    ) {
      // 2. Fire an action to populate redux state with the initial field values
      const fieldValues = extractFieldValues(
        this.screen,
        this.appData.values,
        this.appData.data
      );
      // TDScreenOptionDeprioritizeFieldValues screen param passing values to initial values
      const initialValues = this.screen.params
        ?.split("|")
        ?.includes("TDScreenOptionDeprioritizeFieldValues")
        ? Object.keys(fieldValues).reduce(
            (prevValues, fieldName) => ({
              ...prevValues,
              [fieldName]:
                this.appData.values[fieldName] ?? fieldValues[fieldName]
            }),
            {}
          )
        : fieldValues;

      if (this.appData.showHud) {
        this.appData.resetShowHud();
      }

      if (this.appData.lastNavAction === "next" || isModal) {
        if (this?.screen?.params?.includes("TDScreenOptionStartNewFlow")) {
          this.appData.setFormState(this?.appData?.initialRequest?.formState);
        }
        this.appData.initializeScreen(initialValues);
        if (isModal) this.appData.initializeDialog();
      } else {
        this.appData.initializePrevScreen(initialValues);
      }
      // 4. Reset some values in redux formState if neсessary
      const valuesToReset = this.screen?.data?.state?.resetValues;

      if (valuesToReset && valuesToReset.length) {
        this.appData.deleteFromFormState(valuesToReset);
      }
      // In this case we render nothing (TODO spinner?)
      return null;
    }

    // 5. Add any conditional fields that should be visible
    this.applyConditions();

    // 6. Render react component for screen
    return this.renderScreen({ isRoot: true });
  }

  /**
   * Conditionally formats screen fields and appData values by screen name
   * @method BaseViewController#preprocessScreenData
   */
  preprocessScreenData() {
    if (this.screen.name === "AdvanceMedicalDashboard") {
      if (!this.screen.showClildScreen) {
        const advanceMedicalHomeScreen = this.appData.data.screens.find(
          screen => screen.identifier === "AdvanceMedicalHome"
        );
        if (advanceMedicalHomeScreen) {
          const generalMedicalVisits =
            advanceMedicalHomeScreen.childScreens.filter(childScreen =>
              childScreen.identifier.includes("MyTeladoc.UpcomingConsult")
            );
          if (generalMedicalVisits.length) {
            generalMedicalVisits.forEach(generalMedicalVisit => {
              const { tabs } = generalMedicalVisit.fields.find(
                field => field.type === "tabBar"
              ).data;
              if (tabs.length) {
                // if consult is NOT completed
                const cancelParamsInSnakeCase = Object.keys(
                  generalMedicalVisit.hiddenFields
                )
                  .map(
                    key =>
                      `${camelToSnakeCase(key)}=${encodeURIComponent(
                        generalMedicalVisit.hiddenFields[key]
                      )}`
                  )
                  .join("&");
                const cancelUrl = tabs.find(
                  tab => tab.name === "cancelButton"
                ).url;
                this.screen.fields = [
                  ...generalMedicalVisit.fields,
                  {
                    ...tabs.find(tab => tab.name === "cancelButton"),
                    type: "button",
                    name: "button",
                    links: [
                      {
                        url: `${cancelUrl}${
                          cancelUrl.includes("?") ? "&" : "?"
                        }${cancelParamsInSnakeCase}`
                      }
                    ]
                  },
                  { type: "divider", name: "divider" },
                  ...this.screen.fields
                ];
              } else {
                this.screen.fields = [
                  ...generalMedicalVisit.fields,
                  { type: "divider", name: "divider" },
                  ...this.screen.fields
                ];
              }
            });
          }
        }
        this.screen.showClildScreen = true;
      }
      this.screen.fields = this.screen.fields.map(field => {
        if (
          field.name === "when" &&
          /^\d{2}:\d{2} \/ \d{2}-\d{2}-\d{4}/.test(field.text)
        ) {
          return {
            ...field,
            text: moment(field.text, "hh:mm / DD-MM-YYYY").format(
              "HH:mm dddd, MM-DD-YYYY"
            )
          };
        }
        if (field.name === "start_video") {
          // TODO: add some logic to disable button depends on time
          return { ...field, disabled: false };
        }
        if (field.name === "dashboard_header" && field.image) {
          return { ...field, url: field.image, size: field.text, image: null };
        }
        if (field.name?.includes("consult_message")) {
          return { ...field, name: "status" };
        }
        return field;
      });
    }
    if (this.screen.name === "Dashboard") {
      let webCareTiles = this.screen.fields.filter(field =>
        field?.name?.includes("webCareTile")
      );

      for (let tile in webCareTiles) {
        const className = webCareTiles[tile].data.tile_code.toLowerCase();
        if (webCareTiles[tile].params.includes(className)) continue;
        webCareTiles[tile].params += "|" + className;
      }
    }
    if (this.screen.name === "AdvanceMedicalAddDependent") {
      this.screen.fields = this.screen.fields.map(field => {
        if (field.name === "continueButton") {
          return { ...field, action: this.screen.action };
        }
        return field;
      });
    }
    if (this.screen.name === "AdvanceMedicalAddDependentConfirmation") {
      this.appData.history[0].formState = this.appData.history[1].formState;
    }
    if (this.screen.name === "AdvanceMedicalAppointmentVideoSchedule") {
      this.appData = getAdjustedToCignaAppData(
        this.appData,
        this.screen.conditionals,
        this.screen.fields
      );
      this.screen.fields = getAdjustedToCignaFields(this.screen.fields);
    }
    if (this.screen?.params?.includes("TDScreenOptionSubmitFooter")) {
      if ("footerFields" in this.screen) {
        const cancelButtonField = {
          name: "dismiss_button",
          title: this.appData.settings.translations.cancel,
          type: "callToActionButton",
          params: "TDFieldAutoCorrectNone",
          action: { type: "dismiss" }
        };

        this.screen.footerFields = this.screen.footerFields.filter(
          f => f.name !== "divider"
        );

        const isSaveButtonField = field => field.name.includes("save_button");
        const saveButton = this.screen.footerFields.find(f =>
          isSaveButtonField(f)
        );

        const submitFooterFields = [
          {
            type: "container",
            name: "section-web-sdk submitFooter",
            fieldLayouts: [
              {
                "layout-id": "primary",
                containerFields: [cancelButtonField, saveButton]
              }
            ]
          }
        ];

        if (this.screen.footerFields.some(f => isSaveButtonField(f))) {
          this.screen.footerFields = submitFooterFields;
        }
      }
    }

    if (PRE_PHOTO_MODAL_SCREENS.includes(this.screen.name)) {
      const maxPhotos = this.appData.values.photo_upload_limit
        ? parseInt(this.appData.values.photo_upload_limit, 10)
        : 3;
      // We need this to clean thumbnails if for some reason member click Back button after selecting photo
      for (let i = 1; i < maxPhotos + 1; i++) {
        const prefixPhotoKey = `__photo${i}`;
        const photoPreviewKey = `${prefixPhotoKey}:photoPreview`;
        const photoNameKey = `${prefixPhotoKey}:photoName`;

        if (
          !this.appData.values[photoPreviewKey] ||
          !this.appData.values[photoPreviewKey]?.uuid
        ) {
          removeItemFromIndexedDB(photoPreviewKey);
          this.appData.values[photoPreviewKey] = "";
          this.appData.values[photoNameKey] = "";
        }
      }
    }

    if (
      this.screen.name === "ConsultationNutritionAssessmentOne" ||
      this.screen.name === "Payment" ||
      this.screen.name === "PharmacySearch"
    ) {
      this.appData.values.country_state = "";
    }

    if (this.appData.lastNavAction === "next") {
      if (
        this.screen.name === "AccountCareRecipientsNew" &&
        this.appData.careRecipientStored
      ) {
        const values = extractFieldValues(this.screen, {}, this.appData.data);
        this.appData.values = {
          ...this.appData.values,
          ...values
        };

        this.appData.resetCareRecipient();
      }

      if (
        this.screen.name === "ActivationDependentInfo" &&
        this.appData.dependentStored
      ) {
        const values = extractFieldValues(this.screen, {}, this.appData.data);
        this.appData.values = {
          ...this.appData.values,
          ...values
        };

        this.appData.resetDependent();
      }

      if (
        this.screen.name === "ActivationDependentSuccess" ||
        this.screen.name === "DependentAdded"
      ) {
        this.appData.dependentStoredSuccess();
      }
    }
  }

  /**
   * Binds handleClick and handleChange methods to appData, which is passed down to Screen component as props
   * @method BaseViewController#preprocessAppData
   */
  preprocessAppData() {
    this.appData.handleButtonClick = this.handleButtonClick.bind(this);
    this.appData.handleCheckboxClick = this.handleCheckboxClick.bind(this);
    this.appData.handleCheckboxReturnArrayClick =
      this.handleCheckboxReturnArrayClick.bind(this);
    this.appData.handleAgreementCheckboxClick =
      this.handleAgreementCheckboxClick.bind(this);
    this.appData.handleBackButtonClick = this.handleBackButtonClick.bind(this);
    this.appData.handleSelectChange = this.handleSelectChange.bind(this);
    this.appData.handleSwitchChange = this.handleSwitchChange.bind(this);
    this.appData.handleSliderChange = this.handleSliderChange.bind(this);
    this.appData.handleValuesChange = this.handleValuesChange.bind(this);
    this.appData.handleFileInputChange = this.handleFileInputChange.bind(this);
    this.appData.updateStates = this.updateStates.bind(this);
  }

  handleCheckboxClick(name, checked) {
    this.appData.handleChange({
      [name]: checked
    });
  }

  setValuesFromFieldOptions(field) {
    const selectedOptionsArray = field.options.filter(
      option => option.selected
    );
    const newValuesArray = selectedOptionsArray.map(option => option.value);
    this.appData.handleChange({
      [field.name]: newValuesArray
    });
  }

  handleCheckboxReturnArrayClick(field) {
    const selectedOptionsArray = field.options.filter(
      option => option.selected
    );
    const newValuesArray = selectedOptionsArray.map(option =>
      option.value !== undefined ? option.value : option.text
    );
    this.appData.handleChange({
      [field.name]: newValuesArray
    });
  }

  handleSelectChange(selected, field) {
    const key = Object.keys(selected)[0];
    const CURRENT_LOCATION = "currentLocation";
    const isLocationEnable = field?.options?.some(
      option => option.value === CURRENT_LOCATION
    );
    const defaultLocationValue =
      field?.options?.find(option => option.isCurrentLocation)?.value || "";

    if (key && selected[key] === CURRENT_LOCATION && isLocationEnable) {
      this.appData.getCurrentLocation(key, defaultLocationValue);
    } else if (key && selected[key] === CURRENT_LOCATION) {
      selected[key] = defaultLocationValue || "";
    }
    if (key && selected[key] === "USA") {
      selected = { can_state: "", ...selected };
    }
    if (key && selected[key] === "CAN") {
      selected = { state: "", ...selected };
    }
    this.appData.handleChange(selected);

    if (
      field &&
      field.options &&
      !PREVENT_SELECTED_FIELD_OPTION_UPDATE.includes(field.name)
    ) {
      field.options.forEach(option => {
        if (typeof option === "object")
          option.selected = option.value === selected[key];
      });
    }

    const selectedOptionAction = field?.options?.find(
      option => option.value === Object.values(selected)?.[0]
    )?.action;

    if (selectedOptionAction) {
      this.handleButtonClick(null, null, selectedOptionAction);
    }
  }

  handleAgreementCheckboxClick(name, checked, action) {
    this.appData.handleChange({
      [name]: {
        agreement_id: action.data.agreement_id,
        accepted: checked
      }
    });
  }

  handleAttestationExit(interactive = false) {
    if (
      ["Y", "N"].includes(this.appData.values.attestation_options) &&
      interactive
    ) {
      this.dialog.showDialog({
        actions: [
          {
            label: this.appData.settings.translations.yes,
            onClick: () => {
              const attestationText = this.appData.values.attestation_body_text;
              this.processUscTrackingEvent("Display", {
                content: attestationText?.substr(0, 200)
              });
              this.processUscTrackingEvent("Agreement", { response: "X" });
              this.appData.sendTrackingEvents();
              this.appData.onExit();
            }
          },
          {
            label: this.appData.settings.translations.no,
            className: "secondary",
            name: "close-dialog"
          }
        ],
        content: {
          title: this.appData.settings.translations.you_have_made_selection
        }
      });
    } else {
      const attestationText = this.appData.values.attestation_body_text;
      this.processUscTrackingEvent("Display", {
        content: attestationText?.substr(0, 200)
      });
      this.processUscTrackingEvent("Agreement", { response: "X" });
      this.appData.sendTrackingEvents();
      this.appData.onExit();
    }
  }

  handleButtonClick(
    name,
    text,
    action,
    replaceHistory = 0,
    initialScreen,
    eventStates,
    fetchData = {}
  ) {
    tdLog("BaseViewController.handleButtonClick", name);
    const screenParams = this.screen.params
      ? this.screen.params.split("|")
      : "";
    let formState = { ...fetchData };

    // ******  START webLink handler ****** //
    if (action?.webLink && !action?.is_mobile_route) {
      // webLinks are handled specifically for WEB in case they are present in action
      // NOTE: ported directly from the dfd_master branch

      // webLinks with target_blank are for absolute paths and external links
      if (action?.target_blank) {
        window.open(action.webLink, "_blank");
        return;
      }

      // webLinks without targets are for relative paths and internal links
      const urlSpacer = action.webLink.startsWith("/") ? "" : "/";
      window.location.href = `${window.location.origin}${urlSpacer}${action.webLink}`;
      return;
    }
    // ****** END webLink handler ****** //
    // if ("params" in this.screen) {
    if (
      this.screen?.params?.includes("TDScreenOptionSubmitFooter") &&
      action?.type === "dismiss"
    ) {
      this.appData.prevScreen();
    }
    // }

    if (
      action?.type === "url" &&
      action?.data?.showHUD === true &&
      !this.appData.showHud
    ) {
      this.appData.setShowHud();
    }

    if (action?.type === "selector" && action?.value === "promptToStartOver") {
      this.appData.startOver(this.appData.initialRequest);
      return;
    }

    // TODO: Reference to the screen name should be removed

    if (
      name === "continueButton" ||
      name === "continue_button" ||
      name === "return_to_home" ||
      name === "return_to_home_btn" ||
      name === "return_to_home_select_new_provider_link"
    ) {
      if (
        this.screen.name === "visitCancellation" ||
        this.screen.name === "success" ||
        this.screen.name === "SuccessBpCuffShipmentAddressUpdate" ||
        this.screen.name === "SuccessBpCuffShipmentOptOut"
      ) {
        if (this.appData.data.actions?.[0]?.type === "url") {
          this.appData.nextScreen({
            data: this.appData.data,
            screenIndex: this.appData.screenIndex,
            formState: this.appData.values,
            action: this.appData.data.actions[0],
            replaceHistory: Number.POSITIVE_INFINITY
          });
        } else {
          this.appData.onExit();
        }
        return;
      }
      if (this.screen.name === "VisitUscStudyAttestation") {
        this.handleAttestationExit(true);
        return;
      }

      if (
        this.screen.name === "ConsultationEdit" &&
        name === "return_to_home_select_new_provider_link"
      ) {
        // Button exists only on Consult Reschedule when no slotted providers are available.
        // Give user possibility to go back from there.
        this.appData.onExit();
        return;
      }

      if (
        this.screen.name === "FindBestDoctorSubmitConfirmation" ||
        this.screen.name === "ExpertMedicalOpinionCaseSuccessfullySubmitted" ||
        this.screen.name === "ExpertMedicalOpinionSomeOneElseScreen" ||
        this.screen.name === "MentalHealthNavigatorCaseSuccessfullySubmitted" ||
        this.screen.name === "MentalHealthNavigatorOnlyAdult" ||
        this.screen.name === "MentalHealthNavigatorSomeOneElseScreen" ||
        this.screen.name ===
          "EliteDiagnosticImagingServiceCaseSuccessfullySubmitted" ||
        this.screen.name === "EliteDiagnosticImagingServiceSomeOneElseScreen" ||
        this.screen.name === "EliteDiagnosticImagingServiceOnlyAdult" ||
        this.screen.name ===
          "PersonalHealthNavigatorCaseSuccessfullySubmitted" ||
        this.screen.name === "PersonalHealthNavigatorOnlyAdult" ||
        this.screen.name === "PersonalHealthNavigatorSomeOneElseScreen" ||
        this.screen.name === "PrimaryCareProviderSelectionSuccess" ||
        this.screen.name === "AssessmentCompletedScreenSetting"
      ) {
        this.appData.onExit();
        return;
      }
    }
    if (action?.type === CALL_TO_ACTION_BUTTON_TYPES.goToPreviousScreen) {
      this.appData.prevScreen();
    }

    if (
      action?.type === "close_modal_screen" &&
      !this.appData?.dialogInitialized
    ) {
      this.appData.returnToInbox();
    }

    if (action?.type === "web_url") {
      this.appData.onExit(`/${action.value}`);
      return;
    }

    if (action?.type === "document") {
      this.appData.fetchPostFile({
        url: action.value,
        fileName: text,
        buttonName: name,
        preparedData: {
          ...this.appData.values,
          member_attachment_id: action.data.memberAttachmentId,
          ...action?.data
        }
      });
      return;
    }
    if (action?.type === "webview") {
      downloadFile(action.value, name);
      return;
    }
    if (action?.type === "url" && name.includes("memberAttachments")) {
      this.appData.fetchPostFile({
        url: action.value,
        buttonName: name,
        openInNewTab: true
      });
      return;
    }

    if (
      (action?.type === "call" && !action?.skipCallDialog) ||
      action?.type === "alert"
    ) {
      const dialogContent = { title: action.title || action.type };
      if (action.type === "call") {
        dialogContent.children = (
          <a className='text-link-web-sdk' href={`tel:${action.value}`}>
            {action.value}
          </a>
        );
      }
      if (action.type === "alert") {
        dialogContent.headerClass = "alert-dialog-window";
        dialogContent.label = action.value;
      }

      this.dialog.showDialog({
        actions: [
          action.type === "alert"
            ? {
                label: this.appData.settings.translations.ok,
                className: "primary",
                name: "close-dialog"
              }
            : {
                label: this.appData.settings.translations.cancel,
                className: "secondary",
                name: "close-dialog"
              },
          ...(Array.isArray(action.actions) ? action.actions : [])
        ],
        content: dialogContent
      });
      return;
    }

    if (action?.type === "call" && action?.skipCallDialog) {
      window.location.href = `tel:${action.value}`;
    }

    if (action?.type === "alert_url" && action.data) {
      const {
        cancel_button_title,
        button_title,
        alert_title,
        alert_message,
        consult_id
      } = action.data;

      this.dialog.showDialog({
        actions: [
          {
            label: cancel_button_title,
            className: "secondary",
            name: "close-dialog"
          },
          {
            label: button_title,
            onClick: () =>
              this.handleButtonClick("consult_id", consult_id, {
                type: "url",
                value: action.value
              })
          }
        ],
        content: {
          children: (
            <div className='websdk-pt-4'>
              <div className='websdk-text-3xl websdk-font-bold'>
                {alert_title}
              </div>
              <div className='websdk-text-xl websdk-py-3'>{alert_message}</div>
            </div>
          )
        },
        footerClass: "block"
      });

      return;
    }

    if (action?.type === "pushAlert" && action.data) {
      const {
        cancel_button_title,
        action_button_title,
        value,
        alert_title,
        message_text,
        action: actionType
      } = action.data;
      this.dialog.showDialog({
        actions: [
          {
            label: cancel_button_title,
            className: "secondary",
            name: "close-dialog"
          },
          {
            label: action_button_title,
            onClick: () =>
              this.handleButtonClick("", "", {
                type: actionType,
                value
              })
          }
        ],
        content: {
          title: alert_title,
          label: message_text.includes("{{provider_name}}")
            ? message_text.replace(
                "{{provider_name}}",
                this.screen.fields.find(f => f.name === "name").title
              )
            : message_text
        }
      });
      return;
    }

    if (action?.type === "modalDialogAction" && action.data) {
      const {
        name: modalName,
        modalTitle: title,
        body: label,
        primaryAction,
        primaryActionTitle,
        i18nPrimaryActionLabel = "",
        secondaryAction,
        secondaryActionTitle,
        i18nSecondaryActionLabel = "",
        tertiaryActionTitle
      } = action.data;
      const actions = [
        {
          variant: "primary",
          label: primaryActionTitle,
          updatedRole: primaryAction.type === "url" && "link",
          i18nLabel:
            primaryActionTitle +
            (i18nPrimaryActionLabel && `, ${i18nPrimaryActionLabel}`),
          onClick: () => this.handleButtonClick("", "", primaryAction)
        }
      ];

      if (secondaryActionTitle && secondaryAction) {
        actions.push({
          variant: "secondary",
          label: secondaryActionTitle,
          updatedRole: secondaryAction.type === "url" && "link",
          i18nLabel:
            secondaryActionTitle +
            (i18nSecondaryActionLabel && `, ${i18nSecondaryActionLabel}`),
          onClick: () => this.handleButtonClick("", "", secondaryAction)
        });
      }

      if (tertiaryActionTitle) {
        actions.push({
          variant: "tertiary",
          label: tertiaryActionTitle,
          i18nLabel: `${tertiaryActionTitle}, ${title}`
        });
      }

      this.dialog.showDialog({
        actions: [...actions],
        content: {
          title,
          label,
          name: modalName
        }
      });
      return;
    }

    if (
      name === "exit_bh_matching_tool_link" ||
      name === "see_all_providers_button"
    ) {
      formState = {
        Q_1_1: "",
        Q_1_2: "",
        Q_1_3: "",
        Q_1_4: "",
        Q_1_5: ""
      };
    }

    if (
      (this.screen.name === "BloodPressureMeasurementForm" ||
        this.screen.name === "HeartRateMeasurementForm") &&
      name === "cancelButton"
    ) {
      this.appData.resetFormFields();
      this.appData.resetFieldsValidation();
    }

    let newValues = { ...this.appData.values, ...formState };
    if (text) {
      // Buttons that have a "text" attribute include that value in the formState
      newValues[name] = text;
    }
    if (
      this.screen.params
        ?.split("|")
        ?.includes("TDScreenOptionDeprioritizeFieldValues")
    ) {
      Object.keys(this.appData.data.hidden_fields).forEach(key => {
        if (newValues[key]) {
          this.appData.data.hidden_fields[key] = newValues[key];
        }
      });
    }
    newValues = this.prepareSubmit(newValues);
    if (eventStates?.length) {
      this.handleValuesChange(newValues);
      this.updateStates(eventStates);

      // ForceUpdate may be not needed in this case as handleValuesChange method causes the components to rerender
      // this.TdFlowApp.forceUpdate();
      if (!action?.value) {
        return;
      }
    }

    // We need to preserve the screens below for future usage.
    setSharedScreens(this.appData.data.screens);

    // store button actions for future usage for browser forward button
    if (this.useBrowserNavigation && this.screen.type !== "modal_screen") {
      let screenHistoryAction = action;

      if (this.screen.action?.type === "screen" && this.screen.action.value) {
        screenHistoryAction = {
          ...this.screen.action,
          value: this.appData.data.screens.find(
            screen => screen.identifier === this.screen.action.value
          )?.name
        };
      }

      this.appData.updateScreenHistoryAction({
        index: this.appData.screensHistoryFlow.findIndex(
          screen => screen.name === this.screen.name
        ),
        action: screenHistoryAction
      });
    }

    const confirmationScreens = [
      "VpcpEnhancedSchedulingProviderList",
      "ConsultationVpcAssessmentSix",
      "ExpertMedicalOpinionReviewCaseDetails",
      "MentalHealthNavigatorReviewCaseDetails",
      "PersonalHealthNavigatorReviewCaseDetails",
      "EliteDiagnosticImagingServiceReviewCaseDetails",
      this.appData.isPulse ? "ConsultationSuccess" : "ConsultationConfirmation"
    ];
    const outOfHistoryScreens = ["DermAddTreatment"];

    const getReplaceHistory = () => {
      const isConfirmAction =
        action &&
        !action?.value?.includes("Filter") &&
        !SHORTEN_FLOW_DEPENDANT_SCREENS.includes(action?.value);
      if (
        action?.preNavigation
          ?.split("|")
          ?.includes(ACTION_PARAMS.navDropScreens)
      ) {
        return this.appData.history.findIndex(
          screen => screen.name === action.data?.screen
        );
      }

      if (
        (confirmationScreens.includes(this.screen.name) ||
          screenParams.includes("TDScreenOptionConfirmationScreen")) &&
        isConfirmAction
      ) {
        return Number.POSITIVE_INFINITY;
      }
      if (
        outOfHistoryScreens.includes(
          action?.value || this.screen.action?.value
        ) ||
        outOfHistoryScreens.includes(this.screen.name) ||
        screenParams.includes("TDScreenOptionOutOfHistory")
      ) {
        return 1;
      }
      return replaceHistory;
    };

    // console.log("✏️ Submitting values", newValues);

    if (
      action?.type === "modal_screen" &&
      !this.screen.params?.includes("TDScreenOptionPreventDialog")
    ) {
      const newScreenName = action.value;
      let newScreen = this.appData.data.screens.find(
        screen => screen.name === newScreenName
      );

      if (!newScreen) {
        newScreen = this.appData.data.screens.find(
          screen => screen.identifier === newScreenName
        );
      }

      if (newValues) {
        this.appData.handleChange(newValues);
      }
      newScreen.fields.forEach(field => {
        if (
          field.type === "formTextField" &&
          field.text &&
          !this.appData.values[field.name]
        ) {
          this.appData.handleChange({ [field.name]: field.text });
        }
      });

      this.dialog.showDialog({
        content: {
          modalScreen: true,
          screen: newScreen,
          withClose: true,
          type: action.data?.dialog_class_name
        },
        resettable: isResettableScreen(action.value)
      });
      return;
    }
    if (
      action?.type === "modal_screen" &&
      this.screen.params.includes("TDScreenOptionPreventDialog")
    ) {
      this.appData.nextScreen({
        data: this.appData.data,
        showDialog: this.dialog.showDialog,
        screenIndex: this.appData.screenIndex,
        formState: newValues,
        action,
        replaceHistory: getReplaceHistory.call(this),
        initialScreen,
        isUnifiedRegistration: this.appData.isUnifiedRegistration
      });
      return;
    }

    if (action?.type === "dialog") {
      if (action.value === "dismiss") {
        this.dialog.hideDialog();
        return;
      }

      const dialogName = action?.value;
      const dialogData = this.screen?.dialogs?.find(
        screen => screen?.name === dialogName
      );

      if (dialogData.name === "screenDefault") {
        dialogData.isClosed = null;
        return;
      }

      if (dialogData) {
        this.dialog.showDialog({
          content: {
            dialogScreen: true,
            screen: dialogData,
            withClose: !dialogData?.data?.hasButtons
          }
        });
        return;
      }
    }

    if (
      this.screen?.params?.includes("TDScreenOptionConfirmUnsavedChanges") &&
      name !== "save_button"
    ) {
      const factory = SnapshotModelFactory.for(this.screen.name);
      const result = factory.isEqual(
        this.originalSnapshot,
        this.appData.values
      );

      if (!result.isChanged) {
        showExitDislog.call(
          this,
          () => {
            this.appData.nextScreen({
              data: this.appData.data,
              showDialog: this.dialog.showDialog,
              screenIndex: this.appData.screenIndex,
              formState: this.originalSnapshot,
              action,
              replaceHistory: getReplaceHistory.call(this),
              initialScreen,
              isUnifiedRegistration: this.appData.isUnifiedRegistration
            });
          },
          () => {},
          result.require
        );

        return;
      }
    }

    if (this.screen.type === "modal_screen") {
      this.dialog.hideDialog();
    }

    this.appData.nextScreen({
      data: this.appData.data,
      showDialog: this.dialog.showDialog,
      screenIndex: this.appData.screenIndex,
      formState: newValues,
      action,
      replaceHistory: getReplaceHistory.call(this),
      initialScreen,
      isUnifiedRegistration: this.appData.isUnifiedRegistration
    });

    if (this.screen.name === "AccountCareRecipientsNew") {
      this.appData.careRecipientStoredSuccess();
    }
  }

  handleBackButtonClick() {
    const { history } = this.appData;
    tdLog("BaseViewController.handleBackButtonClick history", history);
    const currentScreen = history[0];
    let num = 1;
    const ignoredScreens = ["ActivationInfo", "MyMedicalHistory"];
    const ignoredUrls = [
      "request_consult/cancel",
      "/cancellation",
      "care_team_relations/destroy",
      "message_center/thread_details_for_consult"
    ];
    const exclusions = [
      "SettingsPrivacyPolicy",
      "SearchMedicationModal",
      "SearchAllergyModal",
      "AddAllergyOther"
    ];

    if (
      this?.screen?.params?.includes("TDScreenHasGoHomeButton") ||
      (history.length <= 1 &&
        !currentScreen.returnBackURL &&
        !this.screen?.params?.includes("TDScreenOptionConfirmUnsavedChanges"))
    ) {
      this.appData.onExit();
    }

    if (
      this.screen.name === "BloodPressureMeasurementForm" ||
      this.screen.name === "HeartRateMeasurementForm"
    ) {
      this.appData.resetFormFields();
      this.appData.resetFieldsValidation();
    }

    while (
      num < history.length - 1 &&
      (history[num].name.includes("Filter") ||
        currentScreen.name === history[num].name ||
        (isSkipableUrl(history[num].url) && history[num].index === 1) ||
        // eslint-disable-next-line no-loop-func
        ignoredUrls.some(url => history[num].url.endsWith(url)) ||
        (ignoredScreens.includes(history[num].name) &&
          !exclusions.includes(currentScreen.name)))
    ) {
      num += 1;
    }

    if (this.screen?.params?.includes("TDScreenOptionConfirmUnsavedChanges")) {
      const factory = SnapshotModelFactory.for(this.screen.name);
      const result = factory.isEqual(
        this.originalSnapshot,
        this.appData.values
      );

      if (!result.isChanged) {
        showExitDislog.call(
          this,
          () => {
            if (
              this.screen?.params
                ?.split("|")
                .includes(UNIFIED_PROFILE_PARAMS.landingPage)
            ) {
              this.appData.onExit();
            } else {
              this.appData.prevScreen(num);
            }
          },
          () => {}
        );

        return;
      }
    }

    if (
      this.screen?.params
        ?.split("|")
        .includes(UNIFIED_PROFILE_PARAMS.landingPage)
    ) {
      this.appData.onExit();
    } else if (!this.screen.hideBackButton) {
      this.appData.prevScreen(num);
    }
  }

  handleSwitchChange = (name, option) => {
    this.appData.handleChange({
      [name]: option.value
    });
  };

  handleSliderChange = (field, selectedOption) => {
    field.options.forEach(option => {
      option.selected = selectedOption.name
        ? option.name === selectedOption.name
        : option.value === selectedOption.value;
    });
    this.appData.handleChange({
      [field.name]: selectedOption.value
    });
  };

  handleValuesChange(values) {
    this.appData.handleChange(values);
  }

  prepareSubmit(values) {
    tdLog("BaseViewController.prepareSubmit", values);
    // Remove conditional fields that are not visible (but were previously and thus have values) from being submitted
    const modifiedValues = { ...values };
    (this.screen.conditionals || []).forEach(condition => {
      const fieldValue = getFieldValue(
        this.appData.values[condition.fieldName]
      );

      if (condition.type === "equal") {
        if (!compareEqualConditional(fieldValue, condition.value)) {
          condition.fields.forEach(field => {
            delete modifiedValues[field.name];
          });
        } else {
          condition.fields.forEach(field => {
            if (field.requestKey) {
              modifiedValues[field.requestKey] = modifiedValues[field.name];
              delete modifiedValues[field.name];
            }
          });
        }
      } else if (condition.type === "notEqual") {
        if (!compareNotEqualConditional(fieldValue, condition.value)) {
          condition.fields.forEach(field => {
            delete modifiedValues[field.name];
          });
        } else {
          condition.fields.forEach(field => {
            if (field.requestKey) {
              modifiedValues[field.requestKey] = modifiedValues[field.name];
              delete modifiedValues[field.name];
            }
          });
        }
      }
    });

    if (modifiedValues.email_address) {
      modifiedValues.email_address_confirmation = modifiedValues.email_address;
    }

    if (this.appData.isUnifiedRegistration && modifiedValues.dob) {
      const { unmasks, brandCountryCode } = this.appData;
      // eslint-disable-next-line new-cap
      const unmaskedDob = unmasks.DateOfBirth(
        modifiedValues.dob,
        brandCountryCode
      );
      modifiedValues.dob = unmaskedDob;
    }

    return modifiedValues;
  }

  applyConditions() {
    this.screen = {
      ...this.screen,
      fields: enableConditionalFields(this.screen, this.appData)
    };
  }

  updateStates = (
    eventStates,
    shouldUpdate = false,
    fields = this.screen.fields
  ) => {
    const conditionalFields = (this.screen.conditionals || [])
      .map(conditional => conditional.fields)
      .flat();

    if (eventStates) {
      const fieldArgs = [...fields, ...conditionalFields];
      updateFieldStates(eventStates, fieldArgs, this.appData);

      if (shouldUpdate && this.TdFlowApp) {
        this.TdFlowApp.forceUpdate();
      }
    }
  };

  /**
   * @method BaseViewController#renderScreen
   * @param {object} screen Contains screen content and settings to be rendered
   * @param {bool} isRoot TODO
   * @returns {React.Component} Renders [Screen]{@link Screen} component
   */
  renderScreen({ screen, isRoot }) {
    const { Screen } = this.components;

    return (
      <Screen
        screen={screen || this.screen}
        appData={this.appData}
        components={this.components}
        isRoot={isRoot}
      />
    );
  }

  processUscTrackingEvent(eventAction, extra_content) {
    const { name: screenName } = this.screen;
    const {
      values: { logged_in_member_id: userId, consultation_id: consult_id }
    } = this.appData;
    const bundleIdentifier = env("BUNDLE_ID");
    const trackingEvent = {
      ...buildTrackingEvent({ bundleIdentifier, userId, screenName }),
      e_a: eventAction,
      e_c: this.appData.settings.translations.usc_study,
      e_n: this.appData.settings.translations.member_attestation,
      consultation_id: consult_id,
      "app-name": "member-websdk",
      ...extra_content
    };

    if (!window?.dataLayer) {
      window.dataLayer = [];
    }

    window.dataLayer.push({
      "Custom event category": trackingEvent.e_c,
      "Custom event action": trackingEvent.e_a,
      event: trackingEvent.e_n,
      "Member ID": userId,
      "Consultation ID": trackingEvent.consultation_id,
      "app-name": trackingEvent["app-name"],
      ...extra_content
    });
  }

  async handleFileInputChange(event, fieldName) {
    const { values, handleChange, data } = this.appData;

    const miniatureWidth = data?.screens?.some(({ name: screenName }) =>
      SCREENS_WITH_MULTIPLE_PHOTO_PICKERS.includes(screenName)
    )
      ? 220
      : 150;

    const fileObj = event.target.files[0];
    /* eslint-disable no-useless-escape */
    const fileName = fileObj.name
      .toLowerCase()
      .replace(/[&\/\\#,+()$~%'":*?<>{}_]/g, "");

    const file = new File([fileObj], fileName);

    if (file.size > 15728640) {
      return;
    }

    let image;
    switch (file.name.split(".").slice(-1)[0]) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        image = await fileToImage(file);
        break;
      case "pdf":
        image = await dataUrlToImage(PdfThumbnail);
        break;
      case "doc":
      case "docx":
        image = await dataUrlToImage(DocThumbnail);
        break;
      case "xls":
      case "xlsx":
        image = await dataUrlToImage(XlsThumbnail);
        break;
      default:
        return;
    }

    const thumbnails = await Promise.all(
      [miniatureWidth, 500].map(size => createThumbnail(image, size, size))
    );
    const fileItem = {
      ...values[fieldName],
      file,
      thumbnails: { [miniatureWidth]: thumbnails[0], 500: thumbnails[1] }
    };
    const changes = {
      [fieldName]: fileItem
    };

    handleChange(changes);
    setItemToIndexedDB(fieldName, fileItem);

    return changes;
  }
}

export default BaseViewController;
